import React, { useCallback, useEffect, useRef, useState } from "react";
import { History } from "lucide-react";
import {
  CompoundActivationOverride,
  CompoundBasePriceChange,
  CompoundPriceHistory,
  CompoundPriceTimelineEntry,
} from "../../model/CompoundPriceHistory";
import { fetchCompoundPriceHistory } from "../../helpers/compoundPriceHistoryApi";
import { eur, marginTone, pct, TONE } from "../../helpers/format";

const SOURCE_LABELS: Record<string, string> = {
  MANUAL_EDIT: "Edição manual",
  EXCEL_IMPORT: "Importação Excel",
  ERP_SYNC: "Sincronização ERP",
  PRICE_ENGINE: "Motor de preços",
  COMPONENT_EDIT: "Edição de componente",
  SYSTEM: "Sistema",
};

const PROMO_LABELS: Record<string, string> = {
  DISCOUNT_PERCENTAGE: "Desconto (%)",
  DISCOUNT_VALUE: "Desconto (€)",
  FIXED_PVP: "PVP Fixo",
};

const TARGET_LABELS: Record<string, string> = {
  NIPOSOM_MARGIN: "Niposom",
  RESELLER_MARGIN: "Reseller",
  BOTH: "Ambas",
};

function money(value: number | null | undefined): string {
  return value != null ? eur.format(value) : "—";
}

function samePrice(
  a: { pvp_with_vat: number | null; reseller_price: number | null },
  b: { pvp: number | null | undefined; reseller: number | null | undefined },
): boolean {
  return (
    a.pvp_with_vat === (b.pvp ?? null) &&
    a.reseller_price === (b.reseller ?? null)
  );
}

function styleMargin(value?: number | null, pctValue?: number | null) {
  if (value == null) return "—";
  const tone = marginTone(pctValue);
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${TONE[tone]}`}
    >
      {eur.format(value)}
      {pctValue != null ? ` · ${pct.format(pctValue)}` : ""}
    </span>
  );
}

function formatDay(value?: string | null): string {
  return value ? new Date(value).toLocaleDateString("pt-PT") : "—";
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("pt-PT");
}

function formatWindow(starts?: string | null, ends?: string | null): string {
  return `${formatDay(starts)} → ${formatDay(ends)}`;
}

/** Background colour + label for a timeline row, by campaign state. */
function entryStyle(entry: CompoundPriceTimelineEntry): {
  rowClass: string;
  label: string;
} {
  if (entry.reason === "CAMPAIGN_END") {
    return {
      rowClass: "bg-gray-100",
      label: entry.campaign_name
        ? `Fim de campanha: ${entry.campaign_name}`
        : "Base",
    };
  }
  if (entry.source !== "CAMPAIGN" || !entry.campaign_name) {
    return { rowClass: "", label: "Base" };
  }
  switch (entry.state) {
    case "SCHEDULED":
      return {
        rowClass: "bg-blue-50",
        label: `Agendada: ${entry.campaign_name}`,
      };
    case "ENDED":
      return {
        rowClass: "bg-gray-100",
        label: `Campanha (terminada): ${entry.campaign_name}`,
      };
    default:
      return {
        rowClass: "bg-amber-50",
        label: `Em campanha: ${entry.campaign_name}`,
      };
  }
}

function PriceRow({
  at,
  pvp,
  reseller,
  rowClass,
  label,
  isCurrent,
}: {
  at: string;
  pvp: number | null | undefined;
  reseller: number | null | undefined;
  rowClass?: string;
  label?: string;
  isCurrent?: boolean;
}) {
  return (
    <div
      className={`flex flex-wrap items-center gap-x-6 gap-y-1 py-3 px-3 rounded ${rowClass ?? ""}`}
    >
      <div className="min-w-[12rem]">
        {isCurrent ? (
          <span className="flex items-center gap-2">
            <span className="rounded-full bg-blue-600 px-2 py-0.5 text-xs font-semibold text-white">
              Agora
            </span>
            <span className="text-xs text-gray-500">{formatDateTime(at)}</span>
          </span>
        ) : (
          <span className="text-xs text-gray-500">{formatDateTime(at)}</span>
        )}
      </div>
      <div className="min-w-[7rem] text-right">
        <span className="block text-xs text-gray-500">PVP c/IVA</span>
        <span className="tabular-nums font-semibold text-green-700">
          {money(pvp)}
        </span>
      </div>
      <div className="min-w-[8rem] text-right">
        <span className="block text-xs text-gray-500">
          Preço Reseller (s/IVA)
        </span>
        <span className="tabular-nums">{money(reseller)}</span>
      </div>
      {label && <div className="text-xs text-gray-600">{label}</div>}
    </div>
  );
}

function ConsolidatedPriceList({ data }: { data: CompoundPriceHistory }) {
  const timeline = data.timeline ?? [];
  const current = data.current_effective;
  const now = Date.now();
  const isFuture = (e: CompoundPriceTimelineEntry) =>
    new Date(e.at).getTime() > now;
  const past = timeline.filter((e) => !isFuture(e)); // already newest-first
  const future = timeline.filter(isFuture).slice().reverse(); // nearest first

  if (timeline.length === 0 && !current) {
    return (
      <p className="text-gray-500 text-sm py-4">
        Sem histórico. O registo começa a partir da primeira alteração de preço
        ou ativação de campanha após esta versão.
      </p>
    );
  }

  const currentFromTimeline = !current && past.length > 0 ? past[0] : null;
  // The row used as "Agora" must not repeat in the history list. When a live
  // current_effective is shown, also drop the campaign-end row of that same
  // campaign: a window edited after activation makes the frozen timeline end
  // before the live one, which would otherwise read "Agora: Em campanha X" right
  // above "Fim de campanha: X".
  let pastRows: CompoundPriceTimelineEntry[];
  if (current) {
    const withoutSameCampaignEnd = past.filter(
      (e) =>
        !(e.reason === "CAMPAIGN_END" && e.campaign_id === current.campaign_id),
    );
    pastRows =
      withoutSameCampaignEnd.length > 0 &&
      samePrice(withoutSameCampaignEnd[0], {
        pvp: current.promotional_pvp_with_vat,
        reseller: current.promotional_reseller_price,
      })
        ? withoutSameCampaignEnd.slice(1)
        : withoutSameCampaignEnd;
  } else {
    // past[0] is the "Agora" row in this case.
    pastRows = past.slice(1);
  }

  return (
    <>
      {current && (
        <div>
          <PriceRow
            at={current.computed_at}
            pvp={current.promotional_pvp_with_vat}
            reseller={current.promotional_reseller_price}
            isCurrent
            rowClass={current.campaign_name ? "bg-amber-50" : ""}
            label={
              current.campaign_name
                ? `Em campanha: ${current.campaign_name}`
                : "Base"
            }
          />
        </div>
      )}
      {currentFromTimeline && (
        <div>
          <PriceRow
            at={currentFromTimeline.at}
            pvp={currentFromTimeline.pvp_with_vat}
            reseller={currentFromTimeline.reseller_price}
            isCurrent
            rowClass={entryStyle(currentFromTimeline).rowClass}
            label={entryStyle(currentFromTimeline).label}
          />
        </div>
      )}

      {pastRows.length > 0 && (
        <ul className="divide-y">
          {pastRows.map((e, i) => (
            <li key={`past-${e.at}-${e.source}-${e.campaign_id ?? 0}-${i}`}>
              <PriceRow
                at={e.at}
                pvp={e.pvp_with_vat}
                reseller={e.reseller_price}
                rowClass={entryStyle(e).rowClass}
                label={entryStyle(e).label}
              />
            </li>
          ))}
        </ul>
      )}

      {!current && !currentFromTimeline && future.length === 0 && (
        <p className="text-gray-500 text-sm py-4">Ainda sem preço registado.</p>
      )}

      {future.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-1">
            Próximas alterações
          </h4>
          <ul className="divide-y">
            {future.map((e, i) => (
              <li key={`future-${e.at}-${e.source}-${e.campaign_id ?? 0}-${i}`}>
                <PriceRow
                  at={e.at}
                  pvp={e.pvp_with_vat}
                  reseller={e.reseller_price}
                  rowClass={entryStyle(e).rowClass}
                  label={entryStyle(e).label}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function DriftBadge({
  baseCaptured,
  mismatchFields,
  erpComparable,
  configuredComparable,
}: {
  baseCaptured: boolean;
  mismatchFields: string[];
  erpComparable: boolean;
  configuredComparable: boolean;
}) {
  if (!baseCaptured) {
    return (
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
        base não capturada
      </span>
    );
  }
  if (!configuredComparable) {
    return (
      <span
        className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
        title="Sem base configurada completa; alinhamento não verificável"
      >
        não verificável
      </span>
    );
  }
  if (!erpComparable) {
    return (
      <span
        className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
        title="Sem referência ERP; alinhamento não verificável"
      >
        não verificável
      </span>
    );
  }
  if (mismatchFields.length > 0) {
    return (
      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-300">
        desalinhado
      </span>
    );
  }
  return (
    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
      ok
    </span>
  );
}

function BaseChangesTable({ rows }: { rows: CompoundBasePriceChange[] }) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-2">
        Sem alterações de preço base registadas.
      </p>
    );
  }
  return (
    <div
      className="overflow-x-auto"
      tabIndex={0}
      role="region"
      aria-label="Alterações de preço base"
    >
      <table className="w-full min-w-[60rem] text-sm">
        <caption className="sr-only">Alterações de preço base por data</caption>
        <thead>
          <tr className="border-b text-left">
            <th scope="col" className="pb-2 font-medium">
              Data
            </th>
            <th scope="col" className="pb-2 font-medium">
              Origem
            </th>
            <th scope="col" className="pb-2 font-medium">
              Campos
            </th>
            <th scope="col" className="pb-2 font-medium text-right">
              PVP c/IVA
            </th>
            <th scope="col" className="pb-2 font-medium text-right">
              Preço Reseller (s/IVA)
            </th>
            <th scope="col" className="pb-2 font-medium text-right">
              Margem Reseller
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b">
              <th
                scope="row"
                className="py-2 font-normal text-left whitespace-nowrap"
              >
                {formatDateTime(r.changed_at)}
              </th>
              <td className="py-2">
                {SOURCE_LABELS[r.source] || r.source}
                {r.actor && (
                  <span className="ml-1 text-xs text-gray-500">
                    ({r.actor})
                  </span>
                )}
              </td>
              <td className="py-2 text-xs text-gray-600">
                {r.changed_fields.join(", ")}
              </td>
              <td className="py-2 text-right tabular-nums">
                <span className="text-gray-400 line-through mr-1">
                  {money(r.old_configured_pvp_with_vat)}
                </span>
                {money(r.new_configured_pvp_with_vat)}
              </td>
              <td className="py-2 text-right tabular-nums">
                <span className="text-gray-400 line-through mr-1">
                  {money(r.old_configured_base_retail_price)}
                </span>
                {money(r.new_configured_base_retail_price)}
              </td>
              <td className="py-2 text-right tabular-nums">
                {styleMargin(r.reseller_margin_value, r.reseller_margin_pct)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivationStatus({
  deactivatedAt,
  startsAt,
  endsAt,
}: {
  deactivatedAt: string | null;
  startsAt: string | null;
  endsAt: string | null;
}) {
  if (deactivatedAt) {
    return (
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
        Encerrada em {formatDay(deactivatedAt)}
      </span>
    );
  }
  if (endsAt && new Date(endsAt).getTime() < Date.now()) {
    return (
      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
        Expirada
      </span>
    );
  }
  if (startsAt && new Date(startsAt).getTime() > Date.now()) {
    return (
      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
        Agendada
      </span>
    );
  }
  return (
    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
      Ativa
    </span>
  );
}

function ActivationsTable({ rows }: { rows: CompoundActivationOverride[] }) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-2">
        Sem ativações de campanha registadas.
      </p>
    );
  }
  return (
    <div
      className="overflow-x-auto"
      tabIndex={0}
      role="region"
      aria-label="Ativações de campanha"
    >
      <table className="w-full min-w-[64rem] text-sm">
        <caption className="sr-only">
          Ativações de campanha que aplicaram este composto
        </caption>
        <thead>
          <tr className="border-b text-left">
            <th scope="col" className="pb-2 font-medium">
              Ativada em
            </th>
            <th scope="col" className="pb-2 font-medium">
              Estado
            </th>
            <th scope="col" className="pb-2 font-medium">
              Campanha
            </th>
            <th scope="col" className="pb-2 font-medium">
              Alvo
            </th>
            <th scope="col" className="pb-2 font-medium text-right">
              PVP promo (c/IVA)
            </th>
            <th scope="col" className="pb-2 font-medium text-right">
              Preço Reseller promo (s/IVA)
            </th>
            <th scope="col" className="pb-2 font-medium">
              ERP
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr
              key={`${a.activation_id}-${a.item_id ?? 0}`}
              className="border-b"
            >
              <th
                scope="row"
                className="py-2 font-normal text-left whitespace-nowrap"
              >
                {formatDateTime(a.activated_at)}
                <span className="block text-xs text-gray-500">
                  {a.is_global ? "Global" : a.reseller_erp_ref || "—"}
                </span>
                {(a.starts_at || a.ends_at) && (
                  <span
                    className="block text-xs text-gray-500"
                    title="Janela agendada na ativação; alterações posteriores da campanha em curso não ficam registadas aqui"
                  >
                    Janela: {formatWindow(a.starts_at, a.ends_at)}
                  </span>
                )}
              </th>
              <td className="py-2">
                <ActivationStatus
                  deactivatedAt={a.deactivated_at}
                  startsAt={a.starts_at}
                  endsAt={a.ends_at}
                />
              </td>
              <td className="py-2">{a.campaign_name}</td>
              <td className="py-2">
                {a.discount_target
                  ? TARGET_LABELS[a.discount_target] || a.discount_target
                  : "—"}
                {a.is_niposom_funded && a.discount_target === "BOTH" && (
                  <span className="ml-1 text-xs text-gray-500">
                    (financiado)
                  </span>
                )}
                <span className="block text-xs text-gray-500">
                  {PROMO_LABELS[a.promo_type] || a.promo_type}
                </span>
              </td>
              <td className="py-2 text-right tabular-nums font-semibold text-green-700">
                {a.promotional_pvp_with_vat != null
                  ? eur.format(a.promotional_pvp_with_vat)
                  : "—"}
                {a.is_niposom_funded &&
                  a.niposom_promo_margin_value != null && (
                    <span className="block text-xs font-normal text-gray-500">
                      margem Niposom{" "}
                      {styleMargin(a.niposom_promo_margin_value, null)}
                    </span>
                  )}
              </td>
              <td className="py-2 text-right tabular-nums">
                {money(a.promotional_reseller_price)}
                {a.reseller_promo_margin_value != null && (
                  <span className="block text-xs text-gray-500">
                    margem {styleMargin(a.reseller_promo_margin_value, null)}
                  </span>
                )}
              </td>
              <td className="py-2">
                <DriftBadge
                  baseCaptured={a.base_captured}
                  mismatchFields={a.mismatch_fields}
                  configuredComparable={
                    a.configured_pvp_with_vat != null &&
                    a.configured_pvp_without_vat != null &&
                    a.configured_base_retail_price != null &&
                    a.configured_base_cost_price != null
                  }
                  erpComparable={
                    a.erp_price_pvp_with_vat != null &&
                    a.erp_price_pvp_without_vat != null &&
                    a.erp_base_retail_price != null &&
                    a.erp_base_cost_price != null
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CompoundPriceHistorySection: React.FC<{ compoundId: string }> = ({
  compoundId,
}) => {
  const [resellerErpRef, setResellerErpRef] = useState("");
  const [data, setData] = useState<CompoundPriceHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqId = useRef(0);

  const load = useCallback(
    async (ref?: string) => {
      const id = ++reqId.current;
      setLoading(true);
      setError(null);
      try {
        const result = await fetchCompoundPriceHistory(compoundId, ref);
        if (id !== reqId.current) return;
        setData(result);
      } catch (err: any) {
        if (id !== reqId.current) return;
        setError(
          err.response?.data?.message || "Erro ao carregar histórico de preços",
        );
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    },
    [compoundId],
  );

  useEffect(() => {
    // Loaded on mount; the parent tab mounts this component only when active, so
    // each open reflects the latest prices.
    load();
  }, [load]);

  const resellerOptions = data
    ? Array.from(
        new Set(
          data.activations
            .filter((a) => !a.is_global && a.reseller_erp_ref)
            .map((a) => a.reseller_erp_ref as string),
        ),
      ).sort()
    : [];

  return (
    <section
      className="bg-white rounded-lg shadow p-6"
      aria-labelledby="compound-price-history-heading"
    >
      <h3 id="compound-price-history-heading" className="sr-only">
        Histórico de preços
      </h3>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-sm">
          <span className="block font-medium text-gray-700 mb-1">
            Reseller (ERP Ref)
          </span>
          <select
            value={
              resellerOptions.includes(resellerErpRef) ? resellerErpRef : ""
            }
            onChange={(e) => {
              setResellerErpRef(e.target.value);
              load(e.target.value || undefined);
            }}
            className="border rounded-lg px-3 py-1.5"
          >
            <option value="">Global (sem reseller)</option>
            {resellerOptions.map((ref) => (
              <option key={ref} value={ref}>
                {ref}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block font-medium text-gray-700 mb-1">
            Outro (ERP Ref)
          </span>
          <input
            type="text"
            value={resellerErpRef}
            onChange={(e) => setResellerErpRef(e.target.value)}
            className="border rounded-lg px-3 py-1.5"
            placeholder="ex: R123"
          />
        </label>
        <button
          type="button"
          onClick={() => load(resellerErpRef || undefined)}
          disabled={loading}
          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:bg-gray-300"
        >
          Aplicar
        </button>
        <span className="flex items-center gap-1 text-gray-500 text-sm ml-auto">
          <History className="w-4 h-4" aria-hidden="true" />
          Preço a cada momento
        </span>
      </div>

      {loading && (
        <p role="status" className="text-gray-500 py-4">
          A carregar histórico…
        </p>
      )}
      {error && (
        <p role="alert" className="text-red-700 py-2">
          {error}
        </p>
      )}

      {!loading && data && (
        <div className="space-y-6">
          <ConsolidatedPriceList data={data} />

          <details className="border-t pt-4">
            <summary className="cursor-pointer text-sm font-medium text-gray-700">
              Detalhe (alterações de base e ativações com ERP)
            </summary>
            <div className="space-y-6 mt-4">
              <div>
                <h4 className="font-semibold mb-2">Alterações de base</h4>
                <BaseChangesTable rows={data.base_changes} />
              </div>
              <div>
                <h4 className="font-semibold mb-2">
                  Ativações de campanha (todas)
                </h4>
                <ActivationsTable rows={data.activations} />
              </div>
            </div>
          </details>
        </div>
      )}
    </section>
  );
};

export default CompoundPriceHistorySection;
